import { useCallback, useSyncExternalStore } from 'react'

/**
 * Subscribes to a CSS media query.
 *
 * Layout differences belong in CSS, and almost all of them here are. This exists for
 * the cases where the two layouts are not the same tree: the filter controls render
 * inline on desktop and inside a bottom sheet on a phone, and a sheet portals to the
 * document body. Rendering both and hiding one would duplicate every control, which
 * means duplicated element ids and two popovers answering to one trigger.
 *
 * Written against `useSyncExternalStore` rather than state plus an effect so the first
 * paint already has the right answer, the same way the theme provider reads the OS
 * colour scheme.
 */
export function useMediaQuery(query: string): boolean {
    const subscribe = useCallback(
        (onChange: () => void) => {
            const media = window.matchMedia(query)
            media.addEventListener('change', onChange)
            return () => media.removeEventListener('change', onChange)
        },
        [query]
    )

    return useSyncExternalStore(
        subscribe,
        () => window.matchMedia(query).matches,
        // No server render in this app; the desktop branch is the safe default anyway.
        () => false
    )
}

/**
 * Below Tailwind's `sm`. Kept as a constant so the breakpoint the JavaScript branches
 * on cannot drift from the `max-sm:` variants that style the same components.
 */
export const MOBILE_QUERY = '(max-width: 39.9375rem)'
